/**
 * useContent — React hook for consuming contentService.
 *
 * Provides loading / error / data states and auto-fetches on mount.
 *
 * Usage:
 *   import { useContent } from '../services';
 *
 *   function Banner() {
 *     const { data, loading, error } = useContent('homeBanner');
 *     if (loading) return <Loader />;
 *     if (error)   return <p>Error loading content</p>;
 *     return <h1>{data.slides[0].headingLine1}</h1>;
 *   }
 *
 * Available keys (maps to contentService methods):
 *   'brandInfo'        → getBrandInfo()
 *   'businessInfo'     → getBusinessInfo()
 *   'socialLinks'      → getSocialLinks()
 *   'navigation'       → getNavigation()
 *   'footerContent'    → getFooterContent()
 *   'siteContent'      → getSiteContent()
 *   'homeBanner'       → getHomeBanner()
 *   'homeFeatures'     → getHomeFeatures()
 *   'homeStory'        → getHomeStory()
 *   'homeOffers'       → getHomeOffers()
 *   'homeSpecialDish'  → getHomeSpecialDish()
 *   'homeSpecials'     → getHomeSpecials()
 *   'homeWhyUs'        → getHomeWhyUs()
 *   'homeVideoSection' → getHomeVideoSection()
 *   'homeTestimonials' → getHomeTestimonials()
 *   'homeReservation'  → getHomeReservation()
 *   'homeRecentUpdates'→ getHomeRecentUpdates()
 *   'homeContent'      → getHomeContent()
 *   'deliciousMenu'    → getDeliciousMenu()
 *   'menuContent'      → getMenuContent()
 *   'teamSection'      → getTeamSection()
 *   'chefs'            → getChefs()
 *   'teamContent'      → getTeamContent()
 *   'aboutContent'     → getAboutContent()
 *   'contactContent'   → getContactContent()
 *   'locations'        → getLocations()
 *   'reservationContent' → getReservationContent()
 */

import { useState, useEffect, useCallback } from 'react';
import contentService from './contentService';

// Map friendly key names → service method names
const KEY_MAP = {
  brandInfo: 'getBrandInfo',
  businessInfo: 'getBusinessInfo',
  socialLinks: 'getSocialLinks',
  navigation: 'getNavigation',
  footerContent: 'getFooterContent',
  siteContent: 'getSiteContent',
  homeBanner: 'getHomeBanner',
  homeFeatures: 'getHomeFeatures',
  homeStory: 'getHomeStory',
  homeOffers: 'getHomeOffers',
  homeSpecialDish: 'getHomeSpecialDish',
  homeLiveSports: 'getHomeLiveSports',
  homeSpecials: 'getHomeSpecials',
  homeWhyUs: 'getHomeWhyUs',
  homeVideoSection: 'getHomeVideoSection',
  homeTestimonials: 'getHomeTestimonials',
  homeReservation: 'getHomeReservation',
  homeRecentUpdates: 'getHomeRecentUpdates',
  homeContent: 'getHomeContent',
  deliciousMenu: 'getDeliciousMenu',
  menuContent: 'getMenuContent',
  teamSection: 'getTeamSection',
  chefs: 'getChefs',
  teamContent: 'getTeamContent',
  aboutContent: 'getAboutContent',
  contactContent: 'getContactContent',
  locations: 'getLocations',
  reservationContent: 'getReservationContent',
};

/**
 * @param {string} contentKey — one of the keys listed above
 * @returns {{ data: any, loading: boolean, error: Error|null, refetch: Function }}
 */
function useContent(contentKey) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchContent = useCallback(async () => {
    const methodName = KEY_MAP[contentKey];
    if (!methodName || typeof contentService[methodName] !== 'function') {
      setError(new Error(`Unknown content key: "${contentKey}"`));
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await contentService[methodName]();
      setData(result);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [contentKey]);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  return { data, loading, error, refetch: fetchContent };
}

export default useContent;
